import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, merge, Observable, switchMap, take } from 'rxjs';

import { ElementsState } from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import { FormState, FormStateContainer } from '@blaze-shared/elements-domain';

import { getElementEntities } from '../../+state/elements/elements.selectors';
import { RuntimeFacade } from '../../+state/runtime/runtime.facade';
import { updateValidationDataMutably } from '../../canvas-elements/io-data/default-handlers/validation-data.utils';
import {
  ComponentValidationData,
  ComponentValidationHandler,
  ConnectValidationConfig,
} from '../../canvas-elements/io-data/io-data.models';
import { ButtonFormState } from '../../canvas-elements/models/render-element.models';
import {
  FormGroupValidityService,
  FormValidationReportService,
  FormValidityService,
} from '../../services/forms/runtime/public-api';

@Injectable({ providedIn: 'root' })
export class ButtonValidationHandler implements ComponentValidationHandler {
  protected readonly formGroupValidity = inject(FormGroupValidityService);
  protected readonly formValidationReport = inject(FormValidationReportService);
  protected readonly formValidity = inject(FormValidityService);
  protected readonly runtimeFacade = inject(RuntimeFacade);
  protected readonly store = inject(Store<ElementsState>);

  validationDataMap: Record<CanvasElementId, ComponentValidationData> = {};

  connectValidation(config: ConnectValidationConfig): Observable<void> {
    const formButtonValidation$ = this.getFormButtonValidation(config);
    const formGroupButtonValidation$ = this.getFormGroupButtonValidation(config);
    return merge(formButtonValidation$, formGroupButtonValidation$);
  }

  getValidationData(elementId: CanvasElementId): ComponentValidationData | undefined {
    return this.validationDataMap[elementId];
  }

  protected getFormButtonValidation({
    element,
    elementInstance,
    setComponentInput,
  }: ConnectValidationConfig): Observable<void> {
    const formlikeValidity$ = this.formValidity.observeFormlikeValidity(
      element.id,
      elementInstance,
    );
    const formState$ = formlikeValidity$.pipe(
      concatLatestFrom(() => this.store.select(getElementEntities)),
      map(([{ containerId, containerType, state }, elements]) => {
        const buttonFormData = this.getButtonFormState(state, containerId, containerType, elements);
        updateValidationDataMutably(this.validationDataMap, element.id, { buttonFormData });
        setComponentInput('formState', state);
      }),
    );
    const dryRuns$ = formlikeValidity$.pipe(
      take(1),
      switchMap(({ containerId }) =>
        this.runtimeFacade.dryRuns$.pipe(
          filter((e) => e.elementId === containerId),
          map((dryRunInProgress) => {
            setComponentInput('delayClick', dryRunInProgress.state);
          }),
        ),
      ),
    );
    return merge(formState$, dryRuns$);
  }

  protected getFormGroupButtonValidation({ element }: ConnectValidationConfig): Observable<void> {
    return this.formGroupValidity.observeFormGroupValidity(element).pipe(
      concatLatestFrom(() => this.store.select(getElementEntities)),
      map(([{ containerId, mainForms, relatedForms }, elements]) => {
        const mainFormsState = mainForms.map(({ id, type, state }) =>
          this.getButtonFormState(state, id, type, elements),
        );
        const relatedFormsState = relatedForms.map(({ id, type, state }) =>
          this.getButtonFormState(state, id, type, elements),
        );
        updateValidationDataMutably(this.validationDataMap, element.id, {
          buttonFormGroupData: {
            formGroupElementId: containerId,
            allFormsState: [...mainFormsState, ...relatedFormsState],
          },
        });
      }),
    );
  }

  private getButtonFormState(
    state: FormState<FormStateContainer> | undefined,
    containerId: string,
    containerType: 'formContainer' | 'editableList' | 'formDetails',
    elements: Dictionary<CanvasElement>,
  ): ButtonFormState {
    return {
      initialized: !!state,
      formElementId: containerId,
      isFormValid: state?.valid !== undefined ? !!state?.valid : true, // form is valid by default
      isFormPosted: !!state?.posted,
      isFormCompleted: state?.completed,
      isFormBlank: !!state?.blank,
      messages: this.formValidationReport.describeErrors(
        containerId,
        containerType,
        state?.errors,
        elements,
      ),
    };
  }
}
